<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient.js";
import ManualSaveControls from "@/components/ManualSaveControls.vue";
import useManualFormState from "@/composables/useManualFormState";
import RequisitionForm from "./RequisitionForm.vue";
import ReferenceComments from "@/components/ReferenceComments.vue";
import ReferenceHistory from "@/components/ReferenceHistory.vue";
import { useAuthStore } from "@/stores/AuthStore";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import dayjs from "dayjs";
import { apiUrl } from "@/vars";
import { SparklesIcon } from "@heroicons/vue/24/solid";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const breadcrumbStore = useBreadcrumbStore();
const requisition = ref(null);
const suppliers = ref([]);
const categories = ref([]);
const form = ref({});
const loading = ref(true);
const paying = ref(false);
const approving = ref(false);
const declining = ref(false);
const files = ref([]);
const filesLoading = ref(false);
const uploadingFiles = ref(false);
const removingFiles = ref({});
const toast = reactive({
  visible: false,
  message: "",
});
let toastTimeout = null;
const canManageAll = computed(
  () => auth.hasAnyRole(["finance-audit", "finance-audits", "super"]),
);
const canViewFinancials = computed(() => auth.hasFinanceRole());
const isOwner = computed(() => {
  const requestedById = requisition.value?.requested_by?.id;
  const userId = auth.user?.id;
  if (!requestedById || !userId) {
    return false;
  }
  return Number(requestedById) === Number(userId);
});
const canEdit = computed(() => canManageAll.value || isOwner.value);
const {
  editable: requisitionEditing,
  saving: requisitionSaving,
  isDirty: requisitionDirty,
  canSave: canSaveRequisition,
  statusText: requisitionStatusText,
  statusTone: requisitionStatusTone,
  sync: syncRequisitionForm,
  startEditing: startEditingRequisition,
  cancelEditing: cancelEditingRequisition,
  startSaving: startSavingRequisition,
  markSaved: markRequisitionSaved,
  markSaveFailed: markRequisitionSaveFailed,
} = useManualFormState(form, {
  leaveMessage: "You have unsaved requisition changes. Leave this page?",
});
const purchaseOrderLabel = computed(() => {
  const value = requisition.value?.purchase_order_number;
  if (!value) {
    return "—";
  }
  return `${value}`;
});
const requisitionTitle = computed(
  () => form.value?.item || requisition.value?.item || "Requisition",
);

const showToast = (message) => {
  toast.message = message;
  toast.visible = true;
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false;
  }, 3000);
};

const loadLookups = async () => {
  const [{ data: supplierData }, { data: categoryData }] = await Promise.all([
    apiClient.get("/api/supplier?page=all"),
    apiClient.get("/api/supplier/categories"),
  ]);
  suppliers.value = supplierData?.data ?? supplierData ?? [];
  categories.value = categoryData;
};

const normalizeSelectId = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const toPounds = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : parsed / 100;
};

const toPence = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : Math.round(parsed * 100);
};

const extractSupplierId = (value) => {
  return value?.supplier_id ?? value?.supplier?.id ?? "";
};

const extractCategoryId = (value) => {
  return value?.category_id ?? value?.category?.id ?? "";
};

const applyRequisitionToForm = (value) => {
  if (!value) {
    return;
  }
  syncRequisitionForm({
    ...value,
    cost_ex_vat: canViewFinancials.value ? toPounds(value.cost_ex_vat) : "",
    supplier_id: normalizeSelectId(extractSupplierId(value)),
    category_id: normalizeSelectId(extractCategoryId(value)),
  });
};

const loadRequisition = async () => {
  const { data } = await apiClient.get(`/api/requisition/${route.params.id}`);
  requisition.value = data?.data ?? data;
  applyRequisitionToForm(requisition.value);
  breadcrumbStore.setValue(requisition.value?.item || "Requisition");
};

const updateForm = (value) => {
  form.value = value;
};

const loadFiles = async () => {
  filesLoading.value = true;
  try {
    const { data } = await apiClient.get(
      `/api/requisition/${route.params.id}/files`,
    );
    files.value = data ?? [];
  } finally {
    filesLoading.value = false;
  }
};

const uploadFiles = async (event) => {
  const selected = Array.from(event.target.files || []).filter(Boolean);
  if (!selected.length) {
    return;
  }
  uploadingFiles.value = true;
  try {
    const formData = new FormData();
    selected.forEach((file) => {
      formData.append("files[]", file);
    });
    const { data } = await apiClient.post(
      `/api/requisition/${route.params.id}/files`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    files.value = [...(data ?? []), ...files.value];
    showToast("Files uploaded.");
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to upload files.");
  } finally {
    uploadingFiles.value = false;
    event.target.value = "";
  }
};

const removeFile = async (file) => {
  if (!confirm(`Remove ${file.filename}?`)) {
    return;
  }
  removingFiles.value[file.id] = true;
  try {
    await apiClient.delete(
      `/api/requisition/${route.params.id}/files/${file.id}`,
    );
    files.value = files.value.filter((entry) => entry.id !== file.id);
    showToast("File removed.");
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to remove file.");
  } finally {
    removingFiles.value[file.id] = false;
  }
};

const openFile = async (file) => {
  try {
    const response = await apiClient.get(`/api/files/${file.id}`, {
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to open file.");
  }
};

const downloadFile = async (file) => {
  try {
    const response = await apiClient.get(`/api/files/${file.id}`, {
      responseType: "blob",
    });
    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.target = "_blank";
    link.rel = "noopener";
    link.download = file.filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to download file.");
  }
};

const saveRequisition = async () => {
  if (!requisitionDirty.value) {
    cancelEditingRequisition();
    return;
  }

  startSavingRequisition();
  try {
    const { data } = await apiClient.post(`/api/requisition/${route.params.id}`, {
      _method: "PUT",
      ...form.value,
      cost_ex_vat: canViewFinancials.value ? toPence(form.value.cost_ex_vat) : null,
    });
    requisition.value = data?.data ?? data;
    applyRequisitionToForm(requisition.value);
    markRequisitionSaved({
      ...form.value,
      cost_ex_vat: canViewFinancials.value
        ? toPounds(requisition.value.cost_ex_vat)
        : "",
    });
    showToast("Requisition saved.");
  } catch (error) {
    markRequisitionSaveFailed(
      error?.response?.data?.message ?? "Unable to save requisition.",
    );
  }
};

const markPaid = async () => {
  if (!confirm("Mark this requisition as paid?")) {
    return;
  }
  paying.value = true;
  try {
    const { data } = await apiClient.put(
      `/api/requisition/${route.params.id}/paid`,
    );
    requisition.value = data?.data ?? data;
    applyRequisitionToForm(requisition.value);
    showToast("Requisition marked as paid.");
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to mark as paid.");
  } finally {
    paying.value = false;
  }
};

const approveRequisition = async () => {
  approving.value = true;
  try {
    const { data } = await apiClient.put(
      `/api/requisition/${route.params.id}/approve`,
    );
    requisition.value = data?.data ?? data;
    applyRequisitionToForm(requisition.value);
    showToast("Requisition approved.");
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to approve requisition.");
  } finally {
    approving.value = false;
  }
};

const declineRequisition = async () => {
  declining.value = true;
  try {
    const { data } = await apiClient.put(
      `/api/requisition/${route.params.id}/decline`,
    );
    requisition.value = data?.data ?? data;
    applyRequisitionToForm(requisition.value);
    showToast("Requisition declined.");
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to decline requisition.");
  } finally {
    declining.value = false;
  }
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }
  return dayjs(value).format("DD MMM YYYY HH:mm");
};

const ensureSelectionsPresent = () => {
  const supplierId = normalizeSelectId(extractSupplierId(requisition.value));
  if (
    supplierId &&
    !suppliers.value.some(
      (supplier) => normalizeSelectId(supplier.id) === supplierId,
    )
  ) {
    suppliers.value = [
      ...suppliers.value,
      {
        id: supplierId,
        company: requisition.value?.supplier?.company ?? "Existing Supplier",
      },
    ];
  }

  const categoryId = normalizeSelectId(extractCategoryId(requisition.value));
  if (
    categoryId &&
    !categories.value.some(
      (category) => normalizeSelectId(category.id) === categoryId,
    )
  ) {
    categories.value = [
      ...categories.value,
      {
        id: categoryId,
        name: requisition.value?.category?.name ?? "Existing Category",
      },
    ];
  }
};

onMounted(async () => {
  await Promise.all([loadLookups(), loadRequisition(), loadFiles()]);
  ensureSelectionsPresent();
  loading.value = false;
});

onBeforeUnmount(() => {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
});
</script>

<template>
  <div class="space-y-4" v-if="!loading">
    <form-box :header="requisitionTitle">
      <template #nav>
        <button
          v-if="canManageAll && !requisition?.payment_issued_at"
          class="btn-plain bg-green-600"
          @click.prevent="markPaid"
          :disabled="paying"
        >
          <span v-if="paying" class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
            Processing...
          </span>
          <span v-else><SparklesIcon class="icon" /> Mark Paid</span>
        </button>
        <button
          v-if="canManageAll"
          class="btn danger"
          @click.prevent="declineRequisition"
          :disabled="declining"
        >
          <span v-if="declining" class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
            Declining...
          </span>
          <span v-else>Decline</span>
        </button>
        <button
          v-if="canManageAll"
          class="btn"
          @click.prevent="approveRequisition"
          :disabled="approving"
        >
          <span v-if="approving" class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
            Approving...
          </span>
          <span v-else>Approve</span>
        </button>
        <ManualSaveControls
          v-if="canEdit"
          :editing="requisitionEditing"
          :saving="requisitionSaving"
          :canSave="canSaveRequisition"
          :statusText="requisitionStatusText"
          :statusTone="requisitionStatusTone"
          @edit="startEditingRequisition"
          @cancel="cancelEditingRequisition"
          @save="saveRequisition"
        />
      </template>
      <RequisitionForm
        :modelValue="form"
        @update:modelValue="updateForm"
        :suppliers="suppliers"
        :categories="categories"
        :readonly="!canEdit || !requisitionEditing"
        :showFinanceFields="canViewFinancials"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-500 mt-4">
        <div class="flex items-center gap-2">
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 aspect-square"
          >
            <img
              v-if="requisition?.requested_by?.avatar_url"
              :src="requisition.requested_by.avatar_url"
              :alt="requisition.requested_by?.name"
              class="block h-full w-full object-cover object-center"
            />
            <span v-else>{{ requisition?.requested_by?.name?.[0] ?? "?" }}</span>
          </div>
          <div>
            <div class="text-xs uppercase text-slate-400">Requested By</div>
            <div class="text-sm text-slate-700 dark:text-slate-200">
              {{ requisition?.requested_by?.name ?? "Unknown" }}
            </div>
          </div>
        </div>
        <div>
          Created: {{ formatDate(requisition?.created_at) }}
        </div>
        <div class="flex flex-col gap-1">
          <div class="uppercase">Purchase Order</div>
          <div
            class="inline-flex items-center gap-2 rounded-full px-3 py-1 font-semibold"
          >
            <span class="uppercase tracking-wide">PO-{{ purchaseOrderLabel }}</span>
          </div>
        </div>
        <div>
          Approved: {{ formatDate(requisition?.approved_to_order_at) }}
        </div>
        <div>
          Payment: {{ requisition?.payment_method ?? "—" }} / {{ formatDate(requisition?.payment_issued_at) }}
        </div>
        <div class="md:col-span-3">
          Declined: {{ formatDate(requisition?.declined_at) }}
        </div>
      </div>
    </form-box>
    <form-box header="Files">
      <div class="flex flex-wrap items-center gap-3">
        <input
          type="file"
          multiple
          class="block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800 dark:text-slate-300"
          @change="uploadFiles"
          :disabled="uploadingFiles || !canEdit"
        />
      </div>
      <div v-if="filesLoading" class="mt-4 text-sm text-slate-500 dark:text-slate-300">
        Loading files...
      </div>
      <div v-else-if="!files.length" class="mt-4 text-sm text-slate-500 dark:text-slate-300">
        No files uploaded yet.
      </div>
      <ul v-else class="mt-4 space-y-2 text-sm">
        <li
          v-for="file in files"
          :key="file.id"
          class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
        >
          <div>
            <button
              type="button"
              class="font-semibold text-cyan-600 hover:underline dark:text-cyan-400"
              @click="openFile(file)"
            >
              {{ file.filename }}
            </button>
            <div class="text-xs text-slate-500 dark:text-slate-400">
              {{ formatDate(file.created_at) }} · {{ file.user?.name ?? "Unknown" }}
            </div>
          </div>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-300"
              @click="openFile(file)"
            >
              View
            </button>
            <button
              type="button"
              class="text-xs font-semibold text-cyan-600 hover:text-cyan-700 dark:text-cyan-400"
              @click="downloadFile(file)"
            >
              Download
            </button>
            <button
              v-if="canEdit"
              class="text-xs font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-300"
              type="button"
              :disabled="removingFiles[file.id]"
              @click="removeFile(file)"
            >
              {{ removingFiles[file.id] ? "Removing..." : "Remove" }}
            </button>
          </div>
        </li>
      </ul>
    </form-box>
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6" v-if="requisition?.reference?.id">
      <ReferenceComments :reference_id="requisition.reference.id" />
      <ReferenceHistory :reference_id="requisition.reference.id" />
    </div>
  </div>
  <div v-else class="box h-64 animate-pulse"></div>
  <div v-if="toast.visible"
    class="fixed bottom-6 right-6 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2">
    <svg class="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
      stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M5 13l4 4L19 7" />
    </svg>
    <span>{{ toast.message }}</span>
  </div>
</template>
